import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, CalendarRange, Globe, MapPin, Lock, User } from "lucide-react";

const MONTHS = [
  { v: 1, l: "Jan" }, { v: 2, l: "Fev" }, { v: 3, l: "Mar" }, { v: 4, l: "Abr" },
  { v: 5, l: "Mai" }, { v: 6, l: "Jun" }, { v: 7, l: "Jul" }, { v: 8, l: "Ago" },
  { v: 9, l: "Set" }, { v: 10, l: "Out" }, { v: 11, l: "Nov" }, { v: 12, l: "Dez" },
];

/**
 * Partner Portal → Temporadas
 * O parceiro vê:
 *   - Temporadas do Admin (readonly, marcadas com escudo)
 *   - As suas próprias temporadas (editáveis, marcadas com "Minha")
 *
 * Em caso de sobreposição de datas, prioridade decide (higher wins).
 */
export default function PartnerSeasons() {
  const [seasons, setSeasons] = useState([]);
  const [zones, setZones] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/partner/seasons"),
    api.get("/public/zones"),
  ]).then(([s, z]) => { setSeasons(s.data); setZones(z.data); });
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    if (s.owner_type !== "partner") return;
    try { await api.patch(`/partner/seasons/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (s.owner_type !== "partner") return;
    if (!window.confirm(`Apagar a sua temporada "${s.name}"?`)) return;
    try { await api.delete(`/partner/seasons/${s.id}`); toast.success("Temporada removida"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const zoneLabel = (key) => zones.find((z) => z.key === key)?.label || key;

  const admin = seasons.filter((s) => s.owner_type !== "partner");
  const own = seasons.filter((s) => s.owner_type === "partner");

  return (
    <div className="p-8 max-w-[1400px]" data-testid="partner-seasons-page">
      <PortalPageHeader
        eyebrow="Pricing · Calendário"
        title="Temporadas"
        description="Configure as suas próprias épocas alta/baixa. As temporadas do Admin são herdadas (readonly). Em caso de sobreposição, a temporada com maior prioridade ganha."
      >
        <button
          type="button"
          onClick={() => { setEditing(null); setShow(true); }}
          className="inline-flex items-center gap-2 h-10 px-5 bg-[#f59e0b] text-white font-bold text-xs uppercase tracking-wide hover:bg-[#d97706]"
          data-testid="new-partner-season-btn"
        >
          <Plus className="w-4 h-4" /> Nova temporada
        </button>
      </PortalPageHeader>

      {/* Own seasons */}
      <div className="mb-8">
        <h3 className="text-[11px] uppercase tracking-widest font-bold text-[#f59e0b] mb-3 flex items-center gap-2">
          <User className="w-3.5 h-3.5" /> As minhas temporadas ({own.length})
        </h3>
        <SeasonTable seasons={own} zoneLabel={zoneLabel} onToggle={toggle} onEdit={(s) => { setEditing(s); setShow(true); }} onDelete={remove} editable emptyMsg="Ainda não criou temporadas próprias." />
      </div>

      {/* Admin seasons */}
      <div>
        <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" /> Herdadas do Admin ({admin.length}) · readonly
        </h3>
        <SeasonTable seasons={admin} zoneLabel={zoneLabel} editable={false} emptyMsg="Sem temporadas globais." />
      </div>

      {show && (
        <SeasonModal
          season={editing}
          zones={zones}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SeasonTable({ seasons, zoneLabel, onToggle, onEdit, onDelete, editable, emptyMsg }) {
  return (
    <div className="border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm min-w-[800px]">
        <thead>
          <tr className="border-b border-slate-200 label-caps text-left">
            <th className="p-4">Nome</th>
            <th className="p-4">Âmbito</th>
            <th className="p-4">Datas</th>
            <th className="p-4 text-right">Prioridade</th>
            <th className="p-4">Estado</th>
            {editable && <th className="p-4"></th>}
          </tr>
        </thead>
        <tbody>
          {seasons.length === 0 && (
            <tr><td colSpan={editable ? 6 : 5} className="p-8 text-center text-slate-400 text-xs italic">{emptyMsg}</td></tr>
          )}
          {seasons.map((s) => (
            <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ptr-season-row-${s.id}`}>
              <td className="p-4 text-[#0a0f1e] font-semibold flex items-center gap-2">
                <CalendarRange className="w-4 h-4 text-[#f59e0b]" /> {s.name}
                {!editable && <Lock className="w-3 h-3 text-slate-400" />}
              </td>
              <td className="p-4">
                {s.scope === "global" ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-600"><Globe className="w-3 h-3" /> Global</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#f59e0b] font-semibold"><MapPin className="w-3 h-3" /> {zoneLabel(s.zone_key)}</span>
                )}
              </td>
              <td className="p-4 text-xs text-slate-700">
                {(s.date_ranges || []).map((r, i) => (
                  <div key={i} className="font-mono">
                    {String(r.start_day).padStart(2, "0")}/{String(r.start_month).padStart(2, "0")} → {String(r.end_day).padStart(2, "0")}/{String(r.end_month).padStart(2, "0")}
                  </div>
                ))}
              </td>
              <td className="p-4 text-right font-mono text-[#0a0f1e]">{s.priority}</td>
              <td className="p-4">
                {editable ? (
                  <button onClick={() => onToggle(s)} className={`px-2 py-1 text-[9px] uppercase font-bold border ${s.active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`} data-testid={`ptr-toggle-season-${s.id}`}>
                    {s.active ? "Activa" : "Inactiva"}
                  </button>
                ) : (
                  <span className={`px-2 py-1 text-[9px] uppercase font-bold border ${s.active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`}>{s.active ? "Activa" : "Inactiva"}</span>
                )}
              </td>
              {editable && (
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => onEdit(s)} className="text-slate-500 hover:text-[#f59e0b]" data-testid={`ptr-edit-season-${s.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(s)} className="text-slate-500 hover:text-red-500" data-testid={`ptr-del-season-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeasonModal({ season, zones, onClose, onSaved }) {
  const isEdit = !!season;
  const [f, setF] = useState(season || {
    name: "", priority: 100, scope: "global", zone_key: "",
    date_ranges: [{ start_month: 6, start_day: 15, end_month: 9, end_day: 15 }],
  });
  const [saving, setSaving] = useState(false);

  const addRange = () => setF({ ...f, date_ranges: [...f.date_ranges, { start_month: 1, start_day: 1, end_month: 1, end_day: 7 }] });
  const rmRange = (i) => setF({ ...f, date_ranges: f.date_ranges.filter((_, idx) => idx !== i) });
  const updRange = (i, k, v) => setF({ ...f, date_ranges: f.date_ranges.map((r, idx) => idx === i ? { ...r, [k]: Number(v) || 1 } : r) });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f };
      if (payload.scope === "global") payload.zone_key = null;
      if (isEdit) {
        await api.patch(`/partner/seasons/${season.id}`, payload);
        toast.success("Temporada actualizada");
      } else {
        await api.post("/partner/seasons", payload);
        toast.success("Temporada criada");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="ptr-season-modal">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl text-[#0a0f1e]">{isEdit ? "Editar temporada" : "Nova temporada"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Nome</label>
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ptr-season-name" placeholder="Ex: Festival Hotel · Verão" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Âmbito</label>
              <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className="input-dt w-full" data-testid="ptr-season-scope">
                <option value="global">Global (todas as zonas)</option>
                <option value="zone">Específica a uma zona</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Prioridade</label>
              <Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: Number(e.target.value) || 100 })} data-testid="ptr-season-priority" />
            </div>
          </div>
          {f.scope === "zone" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Zona</label>
              <select required value={f.zone_key || ""} onChange={(e) => setF({ ...f, zone_key: e.target.value })} className="input-dt w-full" data-testid="ptr-season-zone">
                <option value="">Selecione...</option>
                {zones.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
              </select>
            </div>
          )}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Intervalos de data</label>
              <button type="button" onClick={addRange} className="text-[10px] uppercase text-[#f59e0b] font-bold" data-testid="ptr-add-range">+ Intervalo</button>
            </div>
            <div className="space-y-2">
              {f.date_ranges.map((r, i) => (
                <div key={i} className="border border-slate-200 p-3 flex items-center gap-2" data-testid={`ptr-range-${i}`}>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">De</span>
                  <Input type="number" min="1" max="31" value={r.start_day} onChange={(e) => updRange(i, "start_day", e.target.value)} className="w-16" />
                  <select value={r.start_month} onChange={(e) => updRange(i, "start_month", e.target.value)} className="input-dt">
                    {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">até</span>
                  <Input type="number" min="1" max="31" value={r.end_day} onChange={(e) => updRange(i, "end_day", e.target.value)} className="w-16" />
                  <select value={r.end_month} onChange={(e) => updRange(i, "end_month", e.target.value)} className="input-dt">
                    {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  {f.date_ranges.length > 1 && (
                    <button type="button" onClick={() => rmRange(i)} className="text-red-500 hover:text-red-700 ml-auto" data-testid={`ptr-rm-range-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Intervalos que ultrapassam o fim do ano (ex: 20/Dez → 5/Jan) são suportados.
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="flex-1 h-11 border border-slate-300 text-slate-700 font-bold text-xs uppercase tracking-wide hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={saving} className="flex-1 h-11 bg-[#f59e0b] text-white font-bold text-xs uppercase tracking-wide hover:bg-[#d97706] disabled:opacity-50" data-testid="ptr-save-season">
            {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
